import { Base } from '@studiometa/js-toolkit';

/**
 * @typedef {{
 *   $refs: {
 *     email: HTMLInputElement,
 *     consent: HTMLInputElement,
 *     honeypot: HTMLInputElement,
 *     submit: HTMLButtonElement,
 *     feedback: HTMLElement,
 *     iframe: HTMLIFrameElement,
 *   },
 *   $options: {
 *     successMessage: string,
 *     errorGeneric: string,
 *     errorEmailMissing: string,
 *     errorEmailInvalid: string,
 *     errorConsent: string,
 *   },
 *   $el: HTMLFormElement,
 * }} NewsletterFormProps
 */

/**
 * Formulaire d'inscription à la newsletter Brevo.
 *
 * Le formulaire poste nativement vers l'URL d'action Brevo (attribut `action`)
 * en ciblant un iframe caché (`target`). On reste donc sur la page ; le message
 * de succès s'affiche au chargement de l'iframe après soumission.
 *
 * La réponse Brevo n'est pas lisible (cross-origin) : on valide en amont
 * (email + consentement) et on considère un retour iframe comme un succès.
 * @augments {Base<NewsletterFormProps>}
 */
export default class NewsletterForm extends Base {
  static config = {
    name: 'NewsletterForm',
    refs: ['email', 'consent', 'honeypot', 'submit', 'feedback', 'iframe'],
    // Messages traduisibles, injectés depuis le template via `__()` (data-option-*).
    // Les valeurs par défaut (FR) servent de repli si l'attribut est absent.
    options: {
      successMessage: {
        type: String,
        default: 'Merci, votre inscription est confirmée !',
      },
      errorGeneric: {
        type: String,
        default: 'Veuillez vérifier les informations saisies.',
      },
      errorEmailMissing: {
        type: String,
        default: 'Veuillez renseigner votre adresse e-mail.',
      },
      errorEmailInvalid: {
        type: String,
        default: 'Veuillez saisir une adresse e-mail valide.',
      },
      errorConsent: {
        type: String,
        default: 'Veuillez accepter de recevoir la newsletter pour continuer.',
      },
    },
  };

  /**
   * Vrai uniquement entre une soumission valide et le retour de l'iframe.
   * Évite de déclencher le message de succès au 1er `load` de l'iframe vide.
   * @type {boolean}
   */
  submitted = false;

  /**
   * Validation avant la soumission native vers l'iframe.
   * @param {{ event: SubmitEvent }} payload
   */
  onSubmit({ event }) {
    // Prevent re-entry during active submission
    if (this.submitted) {
      event.preventDefault();
      return;
    }

    // Honeypot rempli => bot : on bloque silencieusement.
    if (this.$refs.honeypot.value) {
      event.preventDefault();
      return;
    }

    // Validation HTML5 (le form est en `novalidate`).
    if (!this.$el.checkValidity()) {
      event.preventDefault();
      this.showFieldError();
      return;
    }

    // OK : on laisse la soumission native partir vers l'iframe.
    this.submitted = true;
    this.setLoading(true);
    this.clearFeedback();
  }

  /**
   * Déclenché au chargement de l'iframe (= réponse Brevo reçue).
   */
  onIframeLoad() {
    if (!this.submitted) {
      return;
    }
    this.submitted = false;
    this.setLoading(false);
    this.showFeedback(this.$options.successMessage, 'success');
    this.$el.reset();
  }

  /**
   * Affiche un message d'erreur ciblé sur le premier champ invalide
   * et y replace le focus.
   */
  showFieldError() {
    const { email, consent } = this.$refs;

    let message = this.$options.errorGeneric;
    let fieldToFocus = email;

    if (email.validity.valueMissing) {
      message = this.$options.errorEmailMissing;
      fieldToFocus = email;
    } else if (email.validity.typeMismatch) {
      message = this.$options.errorEmailInvalid;
      fieldToFocus = email;
    } else if (!consent.checked) {
      message = this.$options.errorConsent;
      fieldToFocus = consent;
    }

    this.showFeedback(message, 'error');
    fieldToFocus.focus();
  }

  /**
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    this.$refs.submit.disabled = isLoading;
    this.$el.dataset.loading = String(isLoading);
  }

  /**
   * @param {string} message
   * @param {'success'|'error'} type
   */
  showFeedback(message, type) {
    const { feedback } = this.$refs;
    feedback.textContent = message;
    feedback.dataset.state = type;
    // Erreur en rouge, succès en noir (panneau blanc dans les deux cas).
    feedback.classList.toggle('text-red-600', type === 'error');
    feedback.classList.toggle('text-black', type !== 'error');
    // Les erreurs doivent être annoncées sans attendre par les lecteurs d'écran.
    feedback.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
    feedback.classList.remove('hidden');
  }

  /**
   *
   */
  clearFeedback() {
    const { feedback } = this.$refs;
    feedback.textContent = '';
    delete feedback.dataset.state;
    feedback.classList.add('hidden');
  }
}
